import { useEffect, useRef, useState } from 'react';
import { GOOGLE_CLIENT_ID } from '../config.ts';

interface GoogleCredentialResponse { credential?: string }
interface GoogleAccounts {
  id: {
    initialize: (options: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void;
    renderButton: (element: HTMLElement, options: Record<string, string | number | boolean>) => void;
    prompt: () => void;
  };
}
declare global { interface Window { google?: { accounts: GoogleAccounts } } }

const GOOGLE_SCRIPT = 'https://accounts.google.com/gsi/client';
let loading: Promise<void> | null = null;

function loadGoogle(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_SCRIPT}"]`);
    const script = existing || document.createElement('script');
    const finish = () => window.google?.accounts ? resolve() : reject(new Error('Google sign-in did not load'));
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Google sign-in did not load')), { once: true });
    if (!existing) {
      script.src = GOOGLE_SCRIPT;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    loading = null;
    document.querySelector(`script[src="${GOOGLE_SCRIPT}"]`)?.remove();
    throw error;
  });
  return loading as Promise<void>;
}

export function GoogleSignIn({ onCredential }: { onCredential: (credential: string) => Promise<void> }) {
  const ref = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadGoogle().then(() => {
      if (cancelled || !ref.current || !window.google?.accounts) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          if (!response.credential || busyRef.current) return;
          busyRef.current = true;
          setBusy(true);
          void onCredential(response.credential)
            .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Could not sign in'))
            .finally(() => { busyRef.current = false; setBusy(false); });
        },
      });
      ref.current.replaceChildren();
      window.google.accounts.id.renderButton(ref.current, {
        type: 'standard', theme: 'outline', size: 'large', shape: 'pill',
        text: 'continue_with', width: Math.min(320, ref.current.clientWidth || 320),
      });
    }).catch(() => { if (!cancelled) setError('Google sign-in could not load. Check your connection and try again.'); });
    return () => { cancelled = true; };
  }, [onCredential]);

  return (
    <div className="google-signin-wrap" aria-busy={busy}>
      <p className="google-signin-label">Sign in with Google to continue</p>
      <div className="google-button-slot">
        <button
          className="google-button-fallback"
          type="button"
          onClick={() => window.google?.accounts.id.prompt()}
          disabled={busy}
        >Continue with Google</button>
        <div ref={ref} className="google-signin" />
      </div>
      {busy && <span className="signin-status">Signing you in…</span>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </div>
  );
}
